import { ScrollingModule } from "@angular/cdk/scrolling";
import { CommonModule } from "@angular/common";
import { Component, inject, Input, OnInit, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NgbActiveModal, NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { AssetAssignmentFilterComponent } from "../../../../../_shared/components/asset-assignment-filter/asset-assignment-filter.component";
import { AssignRequest, ModalConfig, UnassignRequest } from "../../../../driver-vehicle-assignment/models/vehicle-assignment.model";
import { VehicleAssignmentService } from "../../../../driver-vehicle-assignment/services/vehicle-assignment.service";
import { ToastrService } from "ngx-toastr";
import { Asset, DriverAssignmentFilter } from "../../../../../_shared/models";
import { ErrorMessages } from "../../../../../_shared/constants";
import { TranslocoDirective, TranslocoPipe, TranslocoService } from "@jsverse/transloco";

@Component({
    selector: 'app-vehicle-assignment-modal',
    standalone: true,
    imports: [NgbModule, CommonModule, FormsModule, ScrollingModule, AssetAssignmentFilterComponent, TranslocoDirective, TranslocoPipe],
    templateUrl: './vehicle-assignment-modal.component.html',
    styleUrl: './vehicle-assignment-modal.component.scss'
})
export class VehicleAssignmentModalComponent implements OnInit {
    @Input() config: ModalConfig = { mode: 'bulk', action: 'assign', assignmentFilter: {} as DriverAssignmentFilter, assetsLoaded: signal(false) };

    private activeModal: NgbActiveModal = inject(NgbActiveModal);
    private vehicleAssignmentService: VehicleAssignmentService = inject(VehicleAssignmentService);
    private toastr: ToastrService = inject(ToastrService);
    private transloco: TranslocoService = inject(TranslocoService);

    // State
    flatDriversModalList: any[] = [];
    flatAssetsModalList: any[] = [];
    isLoading: boolean = false;
    searchTerm: string = '';
    confirmationText: string = '';

    groupedDrivers: { accountName: string, items: any[], collapsed: boolean }[] = [];
    groupedAssets: { accountName: string, items: any[], collapsed: boolean }[] = [];

    selectedAssets: Array<Asset> = [];

    constructor() { }

    ngOnInit(): void {
        if (this.config.mode === 'bulk') {
            this.initializeBulkMode();
        } else {
            this.initializeSingleMode();
        }
    }

    private initializeBulkMode() {
        if (!this.config.assignmentFilter) return;
        this.groupedDrivers = this.groupByAccount(this.config.assignmentFilter.drivers);
        this.flatDriversModalList = this.getFlatModalList(this.groupedDrivers);

        this.groupedAssets = this.groupByAccount(this.config.assignmentFilter.assets);
        this.flatAssetsModalList = this.getFlatModalList(this.groupedAssets);
    }

    private initializeSingleMode() {
        this.selectedAssets = this.config.assignmentFilter?.assets || [];
        this.groupSelectedAssets();
        this.flattenGroupedAssets();
    }

    // Bulk mode methods
    groupByAccount(entities: { id: number; name: string; accountName: string | undefined }[]) {
        const grouped: { [account: string]: { accountName: string, items: any[], collapsed: boolean } } = {};
        for (const entity of entities) {
            const account = entity.accountName || '';
            if (!grouped[account]) {
                grouped[account] = { accountName: account, items: [], collapsed: false };
            }
            grouped[account].items.push(entity);
        }
        return Object.values(grouped);
    }

    getFlatModalList(grouped: { accountName: string, items: any[], collapsed: boolean }[]): any[] {
        const flat: any[] = [];
        for (const group of grouped) {
            flat.push({ rowType: 'group', accountName: group.accountName, count: group.items.length, collapsed: group.collapsed });
            if (!group.collapsed) {
                for (const item of group.items) {
                    flat.push({ rowType: 'item', ...item });
                }
            }
        }
        return flat;
    }

    onSearch(): void {
        if (this.config.mode !== 'bulk') return;
        const filteredDrivers = this.filterGrouped(this.groupedDrivers, this.searchTerm);
        this.flatDriversModalList = this.getFlatModalList(filteredDrivers);

        const filteredAssets = this.filterGrouped(this.groupedAssets, this.searchTerm);
        this.flatAssetsModalList = this.getFlatModalList(filteredAssets);
    }

    filterGrouped(groups: { accountName: string, items: any[], collapsed: boolean }[], searchTerm: string) {
        if (!searchTerm) {
            return groups;
        }
        const lowerCaseTerm = searchTerm.toLowerCase();
        return groups.map(g => {
            const filteredItems = g.items.filter(x => (x.name || x.assetName || '').toLowerCase().includes(lowerCaseTerm));
            return { ...g, items: filteredItems };
        }).filter(g => g.items.length > 0);
    }

    toggleCollapse(accountName: string, type?: 'driver' | 'asset'): void {
        if (this.config.mode === 'bulk') {
            this.toggleCollapseBulk(accountName, type!);
        } else {
            this.toggleCollapseSingle(accountName);
        }
    }

    private toggleCollapseBulk(accountName: string, type: 'driver' | 'asset'): void {
        if (type === 'driver') {
            const group = this.groupedDrivers.find(g => g.accountName === accountName);
            if (group) {
                group.collapsed = !group.collapsed;
                this.flatDriversModalList = this.getFlatModalList(this.groupedDrivers);
            }
        } else if (type === 'asset') {
            const group = this.groupedAssets.find(g => g.accountName === accountName);
            if (group) {
                group.collapsed = !group.collapsed;
                this.flatAssetsModalList = this.getFlatModalList(this.groupedAssets);
            }
        }
    }

    private toggleCollapseSingle(accountName: string): void {
        const group = this.groupedAssets.find(g => g.accountName === accountName);
        if (group) {
            group.collapsed = !group.collapsed;
            this.flattenGroupedAssets();
        }
    }

    // Single mode methods
    onFilterChanged(filter: any) {
        this.config.assignmentFilter.assets = filter.assets;
        this.selectedAssets = filter.assets || [];
        this.groupSelectedAssets();
        this.flattenGroupedAssets();
    }

    groupSelectedAssets() {
        const groups: { [accountName: string]: any[] } = {};
        for (const asset of this.selectedAssets) {
            const account = asset.accountName || '';
            if (!groups[account]) groups[account] = [];
            groups[account].push(asset);
        }
        this.groupedAssets = Object.entries(groups).map(([accountName, items]) => ({
            accountName,
            items,
            collapsed: false
        }));
    }

    flattenGroupedAssets() {
        const flat: any[] = [];
        for (const group of this.groupedAssets) {
            flat.push({
                rowType: 'group',
                accountName: group.accountName,
                count: group.items.length,
                collapsed: group.collapsed
            });
            if (!group.collapsed) {
                for (const item of group.items) {
                    flat.push({
                        rowType: 'item',
                        name: item.name || item.assetName,
                        accountName: group.accountName
                    });
                }
            }
        }
        this.flatAssetsModalList = flat;
    }

    // Action methods
    async performAction(): Promise<void> {
        if (this.config.action === 'assign') {
            await this.assignAsync();
        } else {
            await this.unassignAsync();
        }
    }

    private async assignAsync(): Promise<void> {
        try {
            this.isLoading = true;
            const request: AssignRequest = this.buildAssignRequest();
            await this.vehicleAssignmentService.assignVehicles(request);
            this.toastr.success(this.transloco.translate('driverShared.assignmentModal.toasts.assignmentSent'));
        } catch (error: any) {
            this.handleErrors(error);
        } finally {
            this.close();
            this.isLoading = false;
        }
    }

    private async unassignAsync(): Promise<void> {
        try {
            this.isLoading = true;
            const request: UnassignRequest = this.buildUnassignRequest();
            await this.vehicleAssignmentService.unassignVehicles(request);
            this.toastr.success(this.transloco.translate('driverShared.assignmentModal.toasts.unassignmentSent'));
        } catch (error: any) {
            this.handleErrors(error);
        } finally {
            this.isLoading = false;
            this.close();
        }
    }

    private buildAssignRequest(): AssignRequest {
        return {
            driverIds: this.config.assignmentFilter.drivers.map(x => x.id),
            assetIds: this.config.assignmentFilter.assets.map(x => x.id)
        }
    }

    private buildUnassignRequest(): UnassignRequest {
        return {
            driverIds: this.config.assignmentFilter.drivers.map(x => x.id),
            assetIds: this.config.assignmentFilter.assets.map(x => x.id),
            confirmation: this.confirmationText
        }
    }

    get modalTitle(): string {
        if (this.config.title) return this.config.title;
        return this.transloco.translate(
            this.config.action === 'assign'
                ? 'vehicleAssignment.modals.assignDriver.title'
                : 'vehicleAssignment.modals.unassignDriver.title'
        );
    }

    get cancelText(): string {
        return this.config.cancelButtonText ?? this.transloco.translate('driverShared.assignmentModal.noCancel');
    }

    get confirmText(): string {
        return this.config.confirmationButtonText ?? this.transloco.translate(
            this.config.action === 'assign'
                ? 'driverShared.assignmentModal.yesAssign'
                : 'driverShared.assignmentModal.yesUnassign'
        );
    }

    get confirmationPlaceholder(): string {
        return this.config.confirmPlaceholder ?? this.transloco.translate('driverShared.assignmentModal.typeUnassignToConfirm');
    }

    get confirmationPrompt(): string {
        return this.config.confirmationPrompt ?? 'unassign'
    }

    get showConfirmationTitle(): boolean {
        return this.config.mode === 'single' && this.config.action === 'unassign';
    }

    get isActionDisabled(): boolean {
        const baseDisabled = this.isLoading;
        if (this.config.showConfirmationMessage) {
            return baseDisabled || this.confirmationText != this.confirmationPrompt;
        }
        return baseDisabled;
    }

    get driversCount(): number {
        return this.config.mode === 'bulk' ? this.config.assignmentFilter?.drivers?.length || 0 : 1;
    }

    get assetsCount(): number {
        if (this.config.mode === 'bulk') {
            return this.config.assignmentFilter?.assets?.length || 0;
        }
        return this.selectedAssets?.length || 0;
    }

    get isAssetsSelected(): boolean {
        return this.selectedAssets.length > 0 || this.config.assignmentFilter?.assets?.length > 0;
    }

    handleErrors(error: any) {
        const errorCode = error?.error?.errorCode;
        const message = ErrorMessages[errorCode] || ErrorMessages['errors/default'];
        this.toastr.error(message);
    }

    close() {
        this.activeModal.close();
    }
}